import { Controller } from "@hotwired/stimulus"
import debounce from "lodash.debounce"
import { appendFilter, isDirty } from "../../helpers/esql_composition"

export default class extends Controller {
  static targets = ["textarea"]
  static outlets = ["logs--state-pill"]
  static values = {
    committed: { type: String, default: "" },
    pendingDirty: { type: Boolean, default: false },
  }

  initialize() {
    // isDirty AST-parses both current and committed text on each call. Without
    // debouncing, rapid typing would re-parse on every keystroke. Click and
    // submit paths still call updatePending() directly for instant feedback —
    // only the input-driven path is throttled.
    this.debouncedUpdatePending = debounce(() => this.updatePending(), 200)
  }

  connect() {
    this.committedValue = this.textareaTarget.value
    this.updatePending()
  }

  disconnect() {
    this.debouncedUpdatePending.cancel()
  }

  onDocumentClick(event) {
    // Require both .filter-button class and the param triplet — narrows from any-data-attr to actual click-to-filter buttons.
    const button = event.target.closest("button.filter-button[data-logs--query-composer-field-param]")
    if (!button) return

    const field = button.getAttribute("data-logs--query-composer-field-param")
    const value = button.getAttribute("data-logs--query-composer-value-param")
    const type = button.getAttribute("data-logs--query-composer-type-param")

    if (field == null || value == null || type == null) return

    this.applyFilter(button, field, value, type)
  }

  applyFilter(button, field, value, type) {
    let result
    try {
      result = appendFilter(this.textareaTarget.value, field, value, type)
    } catch (e) {
      console.warn("[query_composer] appendFilter threw — click skipped", e)
      this.dispatchStatus("error", "Couldn't add filter — unexpected error")
      return
    }

    switch (result.status) {
    case "duplicate":
      this.flashState(button, "duplicate")
      this.dispatchStatus("info", "Filter already applied")
      return
    case "parseError":
      this.flashState(button, "invalid")
      this.dispatchStatus("error", "Couldn't parse query as ES|QL")
      return
    case "ok":
      this.textareaTarget.value = result.text
      this.textareaTarget.dispatchEvent(new Event("input", { bubbles: true }))
      this.flashState(button, "success")
      this.updatePending()
      this.dispatchStatus("success", "Filter added")
      return
    default:
      console.warn("[query_composer] unexpected appendFilter result", result)
      this.flashState(button, "invalid")
      this.dispatchStatus("error", "Couldn't add filter — unexpected response")
      return
    }
  }

  dispatchStatus(type, message) {
    // prefix: "logs" produces a "logs:filter-status" event; bubbles to document
    // where the filter-status controller listens via @document action descriptor.
    this.dispatch("filter-status", { prefix: "logs", detail: { type, message } })
  }

  textareaChanged() {
    this.debouncedUpdatePending()
  }

  pendingDirtyValueChanged(dirty) {
    if (this.hasLogsStatePillOutlet) {
      this.logsStatePillOutlet.modifiedValue = dirty
    }
  }

  updatePending() {
    this.pendingDirtyValue = isDirty(this.textareaTarget.value, this.committedValue)
  }

  flashState(button, state) {
    button.dataset.state = state
    setTimeout(() => {
      delete button.dataset.state
    }, 1200)
  }
}
